import { bookedAlready } from "../../src/booking.ts";
import { iconOf, kindOf } from "../../src/categories.ts";
import type { Item } from "../../src/web/types.ts";

// What the dialog before Remove from trip says (DESIGN.md, Views, "Remove from trip"), its words fitting what goes:
// the item for everyone, with what's on it; then, for a choice, how many options go with it; then what Bookings and
// Money lose, when it (or one of a choice's options) is booked or has payments, and, booked, that removing it doesn't
// cancel the booking, so nobody takes one for the other (the founder, 2026-11-03). Its title names it.
export interface RemoveWords {
  title: string;
  lines: string[];
}

export const REMOVE_FROM_TRIP = "Remove from trip";
export const REMOVE_GOES =
  "It goes from the plan for everyone in the trip, with its ratings, notes, to-dos and files. You can undo it from Updates.";

// What Money counts of it: what's been paid and what a card on file will be charged later.
const payments = (i: Item) => (i.paid?.length ?? 0) + (i.charges?.length ?? 0);
// "its payment comes", "their 2 payments come".
const comeOff = (whose: string, count: number) =>
  `${whose} ${count === 1 ? "payment comes" : `${count} payments come`} off Money.`;
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Who something booked is booked with, to cancel it with, by what the item is (features/map.md, "Remove from trip",
// as built): a stay's hotel (a ryokan, a hostel, a flat's host), a journey's airline or railway by its mode, a place
// by its kind (a restaurant, a bar, a venue for a show or a ticket, a tour's operator), else its operator.
const STAY_WORDS: [RegExp, string][] = [
  [/\bryokan/i, "the ryokan"],
  [/\bhostel/i, "the hostel"],
  [/\bhotel/i, "the hotel"],
  [/\b(?:airbnb|apartment|flat|villa|homestay|guest ?house|minshuku|cottage|cabin)/i, "the host"],
];
// A journey by its mode; a car's (a transfer, a limousine) by its operator.
const BY_MODE: Record<string, string> = {
  plane: "the airline",
  train: "the railway",
  bus: "the bus company",
  ferry: "the ferry company",
  taxi: "the taxi company",
};
const BY_ICON: Record<string, string> = {
  ...BY_MODE,
  bed: "the hotel",
  car: "the rental company",
  bike: "the rental shop",
  coffee: "the café",
  cocktail: "the bar",
  beer: "the bar",
  flag: "the tour operator",
  boat: "the tour operator",
  map: "the tour operator",
  mask: "the venue",
  music: "the venue",
  video: "the venue",
  ball: "the venue",
  mic: "the venue",
  brush: "the school",
  pot: "the school",
  fireworks: "the organiser",
  calendar: "the organiser",
};
const BY_KIND: Record<string, string> = { food: "the restaurant", sights: "the venue", shopping: "the shop" };
export function bookedWith(item: Item): string {
  if (item.type === "stay")
    return STAY_WORDS.find(([words]) => words.test(`${item.category ?? ""} ${item.title}`))?.[1] ?? "the hotel";
  if (item.type === "move") return BY_MODE[iconOf(item)] ?? "the operator";
  if (item.type !== "place") return "the operator";
  return BY_ICON[iconOf(item)] ?? BY_KIND[kindOf(item)] ?? "the operator";
}

// "This doesn't cancel the booking itself: do that with the hotel.", and for several, "the bookings themselves" with
// each of one kind ("each hotel") or every kind named ("the hotel and the airline").
function notCancelled(booked: readonly Item[]): string {
  const kinds = [...new Set(booked.map(bookedWith))];
  const one = booked.length === 1;
  const withWhom = one
    ? kinds[0]
    : kinds.length === 1
      ? `each ${kinds[0]?.replace(/^the /, "")}`
      : `${kinds.slice(0, -1).join(", ")} and ${kinds.at(-1)}`;
  return `This doesn't cancel the ${one ? "booking itself" : "bookings themselves"}: do that with ${withWhom}.`;
}

// `options`: a choice's own (those its card lists), which go with it; for anything else, none.
export function removeWords(item: Item, options: readonly Item[]): RemoveWords {
  const lines = [REMOVE_GOES];
  const going = item.type === "choice" ? options : [];
  const paid = payments(item) + going.reduce((sum, o) => sum + payments(o), 0);
  if (item.type !== "choice") {
    const booked = bookedAlready(item);
    if (booked) {
      lines.push(paid > 0 ? `It's booked, and ${comeOff("its", paid)}` : "It's booked, and goes from Bookings.");
      lines.push(notCancelled([item]));
    } else if (paid > 0) lines.push(capital(comeOff("its", paid)));
    return { title: `Remove ${item.title} from the trip?`, lines };
  }
  const one = going.length === 1;
  if (going.length > 0) lines.push(one ? "Its option goes with it." : `Its ${going.length} options go with it.`);
  const bookedOnes = going.filter(bookedAlready);
  const booked = bookedOnes.length;
  // Which of them is booked: its one option, one of several, or some.
  const which = one ? "The option is booked" : booked === 1 ? "One of them is booked" : `${booked} of them are booked`;
  const whose = one ? "its" : "their";
  if (booked > 0) {
    lines.push(
      paid > 0
        ? `${which}, and ${comeOff(whose, paid)}`
        : `${which}, and ${booked === 1 ? "goes" : "go"} from Bookings.`,
    );
    lines.push(notCancelled(bookedOnes));
  } else if (paid > 0) lines.push(one ? comeOff("The option's", paid) : capital(comeOff(whose, paid)));
  return { title: `Remove ${item.title} from the trip?`, lines };
}
